import {
  CustomItemDataType,
  ProductUniversalItemSource,
  RegistryItemFragment,
  RegistryItemMetadata,
  useCreateRegistryItemFromCatalogItemMutation,
  useCreateRegistryItemMutation
} from '@graphql/generated';
import { useState } from 'react';
import { ApolloError } from '@apollo/client';
import { CatalogContextProps, CatalogDepartmentDictionaryEntry, CurationState, useCatalogContext } from './state/Catalog';
import { CatalogProviderProps } from './state/Catalog';
import { AddRegistryMethodType, CatalogPageTelemetry } from './';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { tryGetStoredCurationForProduct } from './hooks/useStoredCuration';
import { AddRegistryItemFromComponent, CatalogCategoryType, PagesForTelemetry } from './Catalog.telemetry.types';
import { MinimalProductWithDetails } from './utils/createTelemetryPayload';
import { useShippingAddressAndCurrencyDialogsContext } from '../ShippingAddressAndCurrencyDialogsProvider';
import { useCatalogRouting } from './useCatalogRouting';
import { evictChecklistProgressAndSectionsAfterItemAddOrDelete, updateGiftCountsAfterItemAddOrDelete } from '../RegistryGiftCounts/utils';
import { addError } from '@shared/utils/logger';
import { useTrackImpactRadius } from '@shared/utils/impactRadius/useTrackImpactRadius';
import { useCatalogLoggingContext } from './Catalog.logging';
import { Subject } from 'rxjs';
import { isCashOrCharityCatalogItem } from './utils/interpretItemType';
import { AddCatalogItemPurchaseMethod } from './components/ProductDetails/components/BuyForm/BuyForm.types';
import { getCatalogItemId } from './hooks/productPdp';
import { updateRegistriesCacheAfterCreateItem } from '@graphql/mutations/catalog';

export const useCatalogController = () => {
  const { notificationVisible, setNotificationVisible, isMobile } = useCatalogContext();
  const { getRootCatalogPath } = useCatalogRouting();
  const isMobileOrTablet = useIsMobileOrTablet();

  return {
    isMobile,
    isMobileOrTablet,
    notificationVisible,
    setNotificationVisible,
    getRootCatalogPath
  };
};

type UseSharedCatalogMutationsArgs = Readonly<{
  eventId?: string;
  eventHandle: string | null;
  selectedCategory?: Readonly<CatalogDepartmentDictionaryEntry> | null;
  onAddCatalogItem?: (catalogItemId: string) => void;
  onAddCatalogItemSuccess?: () => void;
  onAddCatalogItemError?: (catalogItemId: string, error: ApolloError) => void;
  onAddItemToRegistryWithoutEvent?: CatalogProviderProps['onAddItemToRegistryWithoutEvent'];
}>;

export type UseSharedCatalogMutationReturn = ReturnType<typeof useSharedCatalogMutation>;

// partially matches RegistryItemSource in registry_service
export enum RegistryItemSource {
  googleShoppingSponsored = 'googleShoppingSponsored',
  googleShoppingRegular = 'googleShoppingRegular',
  googleSearch = 'googleSearch',
  catalogSearch = 'catalogSearch',
  catalogShop = 'catalogShop',
  manual = 'manual',
  manualScanned = 'manualScanned',
  recommendedCatalogTransfer = 'recommendedCatalogTransfer',
  recommendedCatalogShop = 'recommendedCatalogShop',
  adopt = 'adopt',
  bookmarklet = 'bookmarklet'
}

type RegistryItemMetadataWithSource = Merge<
  RegistryItemMetadata,
  {
    source: RegistryItemSource;
  }
>;

export type AddCatalogItemToRegistryPayload = {
  qty: number;
  product: MinimalProductWithDetails;
  itemPosition?: number;
  curation?: Maybe<CurationState>;
  category?: Maybe<CatalogContextProps['selectedCategory']>;
  metadata: RegistryItemMetadataWithSource;
  purchaseMethod?: AddCatalogItemPurchaseMethod | null;
  /**
   * Passing `false` will skip telemetry log
   */
  telemetryArgs: {
    method: AddRegistryMethodType;
    page: CatalogPageTelemetry;
    categoryOverride?: string;
    productCatalogCategoryTypeOverride?: CatalogCategoryType;
    cashFundRelatedProduct?: string;
    origin?: string;
    isOnboarding?: boolean;
  } & (AddRegistryItemFromComponent | {});
  onAddComplete?: () => void;
};

/**
 * This payload is unique since it's based off a shopping search result, not a catalog item
 */
export type AddUniversalSearchItemToRegistryPayload = {
  component: 'productGrid' | 'universalSearch';
  qty: number;
  itemPosition: number;
  searchTerm: string;
  /**
   * Custom user title
   */
  title: string;
  priceAsFloatingPointDecimalString: string;
  note: string;
  productImageUrl: string;
  productUrl: string;
  shopName: string;
  metadata: RegistryItemMetadataWithSource;
  /**
   * Shopping search result title
   */
  productTitle: string;
  productSource?: ProductUniversalItemSource;
  page?: PagesForTelemetry;
};

type AddRegistryItemPayload = Omit<AddCatalogItemToRegistryPayload, 'curation' | 'category'>;

export type AddRegistryItemSource = Pick<AddRegistryItemPayloadBySource, 'source'>['source'];

export type AddRegistryItemPayloadBySource =
  | {
      source: 'pdp' | 'pdpModal' | 'catalog';
      payload: AddRegistryItemPayload;
    }
  | {
      // Adding a separate source because it does not use a Joy catalog item
      source: 'universalSearch';
      payload: AddUniversalSearchItemToRegistryPayload;
    };

type CreateRegistryItemSubject = {
  purchaseMethod?: AddCatalogItemPurchaseMethod | null;
  item: RegistryItemFragment;
};

export const createRegistryItemSubject = new Subject<CreateRegistryItemSubject>();
const catalogItemIdsForPurchase = new Map<string, AddCatalogItemPurchaseMethod>();

export const useSharedCatalogMutation = ({
  eventId: eventIdArg,
  eventHandle,
  onAddCatalogItemError,
  onAddCatalogItemSuccess,
  onAddItemToRegistryWithoutEvent,
  selectedCategory
}: UseSharedCatalogMutationsArgs) => {
  const [notificationVisible, setNotificationVisible] = useState<boolean>(false);
  const { trackAddRegistryItemAction, trackAddUniversalSearchItemAction } = useCatalogLoggingContext();

  const trackImpactRadius = useTrackImpactRadius();

  const [createRegistryItemFromCatalog, { loading: addCatalogItemLoading, error: addCatalogItemError }] = useCreateRegistryItemFromCatalogItemMutation({
    onCompleted(data) {
      const item = data.createRegistryItemFromCatalogItem.model;
      if (!item) {
        return;
      }
      const pdpUrl = item.productData.pdpUrl;
      let purchaseMethod = null;
      if (pdpUrl) {
        const catalogItemId = getCatalogItemId({ pdpUrl });
        if (catalogItemId) {
          purchaseMethod = catalogItemIdsForPurchase.get(catalogItemId);
          catalogItemIdsForPurchase.delete(catalogItemId);
        }
      }

      createRegistryItemSubject.next({
        purchaseMethod,
        item
      });
    }
  });
  const [createCustomRegistryItem, { loading: createRegistryItemLoading }] = useCreateRegistryItemMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries', 'GetRegistryOnboardingSuggestions', 'RegistryGiftBasketCountByEventName'],
    awaitRefetchQueries: true,
    onCompleted(data) {
      createRegistryItemSubject.next({
        item: data.createRegistryItem
      });
    }
  });

  const { verifyAddressAndTryAddCatalogItem } = useShippingAddressAndCurrencyDialogsContext();

  // Internal function intended to only be used in `guardedAddCatalogItemToRegistry`.
  const addCatalogItemToRegistry = useEventCallback(async (eventId: string, { source, payload }: AddRegistryItemPayloadBySource) => {
    /**
     * Shared actions to take regardless of add source
     */
    const afterSuccessfulAdd = () => {
      trackImpactRadius('Product_Added');
      setNotificationVisible(true);
      onAddCatalogItemSuccess?.();
    };

    if (source === 'universalSearch') {
      // TODO: Handle universal search
      // 1. add to registry ✅
      // 2. log action - https://withjoy.atlassian.net/browse/BABY-668
      // 3. impact radius

      try {
        // This add flow is similar to that of a custom gift or browser button product add.
        const { qty, note, title, productUrl, productImageUrl, metadata, priceAsFloatingPointDecimalString } = payload;

        trackAddUniversalSearchItemAction({
          ...payload,
          eventId
        });
        await createCustomRegistryItem({
          update: cache => {
            // TODO: Mirror the existing behavior
            // updateGiftCountsAfterItemAddOrDelete({ eventHandle, cache, qty: quantity, valueInMinorUnits: product.price?.valueInMinorUnits, isDelete: false });
            // evictChecklistProgressAndSectionsAfterItemAddOrDelete({ eventId, cache });
            // https://withjoy.atlassian.net/browse/BABY-669
          },
          variables: {
            eventId,
            payload: {
              totalRequested: qty,
              note,
              custom: {
                type: CustomItemDataType.gift,
                title,
                url: productUrl,
                imageUrlForImport: productImageUrl,
                priceAsFloatingPointDecimalString
              },
              metadata
            }
          }
        });

        afterSuccessfulAdd();
      } catch (e) {}
    } else {
      const { itemPosition, qty, product, telemetryArgs, metadata, purchaseMethod } = payload;
      if (product) {
        // I Heart Radio ad campaign tracking pixel function block
        (function () {
          const iTag = document.createElement('img');
          iTag.setAttribute('src', '//arttrk.com/pixel/?ad_log=referer&action=registration&pixid=aad0d74e-06ee-4d0e-8000-408c8e31396a');
          iTag.setAttribute('width', '1');
          iTag.setAttribute('height', '1');
          document.body.appendChild(iTag);
        })();
        const catalogItemId = product.id;
        const quantity = Math.max(qty, 1);

        try {
          if (telemetryArgs) {
            trackAddRegistryItemAction({
              ...telemetryArgs,
              eventId,
              itemPosition,
              product,
              qty
            });
          }
          if (purchaseMethod) {
            catalogItemIdsForPurchase.set(catalogItemId, purchaseMethod);
          }

          await createRegistryItemFromCatalog({
            refetchQueries: [
              // Refetch needed cause we don't have all the data and conext needed to update the cache of gift counts
              'RegistryGiftBasketCountByEventName'
            ],
            update: (cache, result) => {
              updateRegistriesCacheAfterCreateItem({ cache, result: result.data, eventHandle });
              updateGiftCountsAfterItemAddOrDelete({ eventHandle, cache, qty: quantity, valueInMinorUnits: product.price?.valueInMinorUnits, isDelete: false });
              evictChecklistProgressAndSectionsAfterItemAddOrDelete({ eventId, cache });
            },
            variables: {
              eventId,
              payload: {
                totalRequested: quantity,
                catalogItemId,
                catalogCategoryId: selectedCategory?.id,
                metadata
              }
            }
          });

          afterSuccessfulAdd();
        } catch (error: unknown) {
          onAddCatalogItemError?.(catalogItemId, error as ApolloError);
          addError(error, { action: 'addCatalogItemToRegistry', product, eventHandle });
        }
      }
    }
  });

  const guardedAddCatalogItemToRegistry = useEventCallback((input: AddRegistryItemPayloadBySource) => {
    const { source, payload } = input;
    if (source !== 'universalSearch' && isCashOrCharityCatalogItem(payload.product)) {
      // TODO: Add logging, we don't want to automatically add cash/charity products -- user needs to provide info.
      return;
    }

    if (eventIdArg) {
      // Only when there is an event selected should we verify the address
      verifyAddressAndTryAddCatalogItem(() => {
        addCatalogItemToRegistry(eventIdArg, input);
      }, payload.qty);
    } else {
      if (source === 'catalog' || source === 'pdpModal') {
        const { itemPosition, qty, product, metadata, telemetryArgs, purchaseMethod } = payload;
        const storedCuration = tryGetStoredCurationForProduct(product);
        const catalogLocator = storedCuration?.publicUrlPath || selectedCategory?.publicUrlPath;

        onAddItemToRegistryWithoutEvent?.({
          type: 'catalog',
          itemType: 'product',
          catalogLocator,
          purchaseMethod,
          qty,
          itemPosition,
          product,
          telemetryArgs,
          ...(storedCuration ? { curation: storedCuration, category: null } : { curation: null, category: selectedCategory }),
          metadata
        });
        return;
      } else if (source === 'universalSearch') {
        // New behaviour as of 11/20/23
        onAddItemToRegistryWithoutEvent?.({
          type: 'universalSearch',
          ...payload
        });
      } else {
        const { itemPosition, qty, product, telemetryArgs, metadata, purchaseMethod } = payload;
        const storedCuration = tryGetStoredCurationForProduct(product);
        /**
         * This will trigger one of two things:
         * - The event picker is opened
         * - The user is redirect to create wedding
         */
        onAddItemToRegistryWithoutEvent?.({
          type: 'item',
          qty,
          itemPosition,
          product,
          purchaseMethod,
          telemetryArgs,
          metadata,
          ...(storedCuration ? { curation: storedCuration, category: null } : { curation: null, category: selectedCategory })
        });
      }
    }
  });

  return {
    addCatalogItemLoading: addCatalogItemLoading || createRegistryItemLoading,
    addCatalogItemError,
    /**
     * - If user is not logged in, redirect to /createwedding
     * - If logged in:
     *    - If no event selected, open event selector
     *    - If event selected, verify shipping address + try to add item
     */
    addCatalogItemToRegistry: guardedAddCatalogItemToRegistry,
    notificationVisible,
    setNotificationVisible
  };
};
